import React, { useState, useEffect, useRef } from 'react';
import { FaTimes, FaPlus } from 'react-icons/fa';
import '../styles/NewChatModal.css';

const NewChatModal = ({ isOpen, onClose, onConfirm, buttonRef }) => {
  const [chatName, setChatName] = useState('');
  const [error, setError] = useState('');
  const modalRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setChatName('');
      setError('');
      // Focus the input when modal opens
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 100);
    }
  }, [isOpen]);

  // Handle click outside to close
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const trimmedName = chatName.trim();
    
    if (!trimmedName) {
      setError('Please enter a chat name');
      return;
    }

    if (trimmedName.length > 100) {
      setError('Chat name must be less than 100 characters');
      return;
    }

    onConfirm(trimmedName);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  if (!isOpen) return null;

  // Calculate position relative to button
  const getModalPosition = () => {
    if (!buttonRef?.current) return {};
    
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const modalWidth = 320;
    const modalHeight = 200;
    
    // Position to the right of the button with some spacing
    let left = buttonRect.right + 15;
    let top = buttonRect.top + (buttonRect.height / 2) - (modalHeight / 2);
    
    // Ensure modal doesn't go off screen
    const windowWidth = window.innerWidth;
    const windowHeight = window.innerHeight;
    
    if (left + modalWidth > windowWidth - 20) {
      // Position to the left of the button instead
      left = buttonRect.left - modalWidth - 15;
    }
    
    if (top < 20) {
      top = 20;
    } else if (top + modalHeight > windowHeight - 20) {
      top = windowHeight - modalHeight - 20;
    }

    return {
      left: `${left}px`,
      top: `${top}px`,
    };
  };

  return (
    <div className="new-chat-modal-overlay">
      <div 
        className="new-chat-modal" 
        ref={modalRef}
        style={getModalPosition()}
      >
        <div className="new-chat-modal-header">
          <h3>
            <FaPlus className="modal-icon" />
            Create New Chat
          </h3>
          <button 
            className="close-button" 
            onClick={onClose}
            type="button"
          >
            <FaTimes />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="new-chat-modal-form">
          <div className="form-group">
            <label htmlFor="chatName">Chat Name</label>
            <input
              ref={inputRef}
              id="chatName"
              type="text"
              value={chatName}
              onChange={(e) => {
                setChatName(e.target.value);
                setError('');
              }}
              onKeyPress={handleKeyPress}
              placeholder="Enter a name for your chat..."
              className={error ? 'error' : ''}
              maxLength={100}
            />
            {error && <span className="error-text">{error}</span>}
          </div>
          
          <div className="form-actions">
            <button 
              type="button" 
              className="cancel-button"
              onClick={onClose}
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="create-button"
              disabled={!chatName.trim()}
            >
              Create Chat
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewChatModal; 